import { NetworkState } from './offlineDataTypes';

export type DataSaverPreference = 'AUTO' | 'ON' | 'OFF';

class NetworkStateEngine {
  private currentState: NetworkState = 'ONLINE';
  private dataSaver: DataSaverPreference = 'AUTO';
  private listeners: ((state: NetworkState, isDataSaver: boolean) => void)[] = [];

  constructor() {
    if (typeof window !== 'undefined') {
      this.currentState = navigator.onLine ? 'ONLINE' : 'OFFLINE';
      
      window.addEventListener('online', () => {
        this.currentState = 'RECOVERING';
        this.notifyListeners();
        // Confirm actual connectivity with a lightweight ping
        this.verifyConnectivity();
      });

      window.addEventListener('offline', () => {
        this.currentState = 'OFFLINE';
        this.notifyListeners();
      });

      // Load data saver preference from localStorage
      try {
        const saved = localStorage.getItem('yamoopay_data_saver');
        if (saved === 'ON' || saved === 'OFF' || saved === 'AUTO') {
          this.dataSaver = saved;
        }
      } catch (e) {
        // ignore storage error
      }
    }
  }

  public getNetworkState(): NetworkState {
    return this.currentState;
  }

  public isDataSaverActive(): boolean {
    if (this.dataSaver === 'ON') return true;
    if (this.dataSaver === 'OFF') return false;
    // Auto: Check NetworkInformation saveData if supported
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
      const conn = (navigator as any).connection;
      if (conn && (conn.saveData === true || conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g')) {
        return true;
      }
    }
    return false;
  }

  public getDataSaverPreference(): DataSaverPreference {
    return this.dataSaver;
  }

  public setDataSaverPreference(pref: DataSaverPreference) {
    this.dataSaver = pref;
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('yamoopay_data_saver', pref);
      }
    } catch (e) {
      // ignore
    }
    this.notifyListeners();
  }

  public async verifyConnectivity(): Promise<boolean> {
    if (typeof window === 'undefined' || !navigator.onLine) {
      this.currentState = 'OFFLINE';
      this.notifyListeners();
      return false;
    }

    try {
      // Quick cache-busting HEAD or GET request to verify live internet
      const res = await fetch('/manifest.webmanifest?ping=' + Date.now(), { method: 'HEAD', cache: 'no-store' });
      if (res.ok) {
        this.currentState = 'ONLINE';
      } else {
        this.currentState = 'DEGRADED';
      }
    } catch (e) {
      this.currentState = 'OFFLINE';
    }

    this.notifyListeners();
    return this.currentState === 'ONLINE';
  }

  public subscribe(callback: (state: NetworkState, isDataSaver: boolean) => void): () => void {
    this.listeners.push(callback);
    callback(this.currentState, this.isDataSaverActive());
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyListeners() {
    const isDataSaver = this.isDataSaverActive();
    this.listeners.forEach(l => l(this.currentState, isDataSaver));
  }
}

export const networkEngine = new NetworkStateEngine();
